import { TypeReference } from '../types.js';
import { PropertyPath } from '../engine.js';
import { CompatibilityRule, CompatibilityTable, OptionHashSelector, RuleHashSelector } from './CompatibilityTable.js';
import { Hash } from './CompatibilityTable.js';
import { Option } from '../option.js';

type RuleNo = number & { brand: 'RuleNo' };
type OptionValueIndex = Map<Hash, Set<RuleNo>>

function makeRuleNo(value: number): RuleNo {
  return value as RuleNo;
}

export class IndexedRules {
  private optionHashCache: Map<Option, Map<PropertyPath, Hash>> = new Map<Option, Map<PropertyPath, Hash>>();

  private currentRules = new Set<RuleNo>();
  // private currentRulesHashes = new Set<Hash>();
  private appliedFilters = new Map<PropertyPath, Hash>();
  private allRulesSet: Set<RuleNo>;

  constructor(
    private table: CompatibilityTable
  ) {
    this.init();
  }

  /**
   * List of propertyPaths of all options involved in the rules
   */
  involvedOptionPaths: TypeReference[] = [];
  /**
   * Hash function for rule with specified propertyPath of option
   */
  ruleSelectors = new Map<PropertyPath, RuleHashSelector>();
  /**
   * Hash function for option with specified propertyPath
   */
  optionSelectors = new Map<PropertyPath, OptionHashSelector>();
  /**
   * For each option propertyPath contains map between optionHash and list of rules in which it is involved
   */
  indexes = new Map<PropertyPath, OptionValueIndex>();
  rulesLinks: Array<Map<PropertyPath, Hash>> = []
  appliedHashes = new Map<PropertyPath, Set<RuleNo>>();

  init() {
    this.allRulesSet = new Set<RuleNo>();
    for (let i = 0; i < this.table.rules.length; i++) {
      this.allRulesSet.add(makeRuleNo(i));
    }
    this.currentRules = this.allRulesSet;
    const optionPaths = Array.from(this.table.getCompatibilityInvolvedOptionPaths());
    this.involvedOptionPaths = optionPaths;
    for (const optionPath of optionPaths) {
      const { optionHashSelector, ruleHashSelector } = this.table.createOptionPropertiesSelector(optionPath);

      this.ruleSelectors.set(optionPath, ruleHashSelector);
      this.optionSelectors.set(optionPath, (option: Option) => {
        let map = this.optionHashCache.get(option)
        if (!map) {
          map = new Map<PropertyPath, Hash>();
          this.optionHashCache.set(option, map);
        }
        let optionHash = map.get(optionPath);
        if (!optionHash) {
          optionHash = optionHashSelector(option);
          map.set(optionHash, optionHash);
        }
        return optionHash;
      });
    }
    this.hashRules(this.table.rules);
  }

  hashRules(rules: CompatibilityRule[]) {
    for (const optionPath of this.involvedOptionPaths) {
      this.indexes.set(optionPath, new Map<Hash, Set<RuleNo>>());
    }
    for (let i = 0; i < rules.length; i++) {
      let rule = rules[i];
      this.rulesLinks[i] = new Map<PropertyPath, Hash>();
      for (const optionPath of this.involvedOptionPaths) {
        const ruleHashForOption = this.ruleSelectors.get(optionPath)(rule);
        const indexHash = this.indexes.get(optionPath);
        let indexHashRules = indexHash.get(ruleHashForOption);
        if (!indexHashRules) {
          indexHashRules = new Set();
          indexHash.set(ruleHashForOption, indexHashRules);
        }
        indexHashRules.add(makeRuleNo(i));
        this.rulesLinks[i].set(optionPath, ruleHashForOption);
      }
    }
  }

  matchOne(optionPath: PropertyPath, optionHash: Hash) {
    const optionRules = this.indexes.get(optionPath).get(optionHash);
    if (!optionRules) {
      return false; // option is not in the list of rules
    }
    for (let rule of optionRules) {
      if (this.currentRules.has(rule)) {
        return true;
      }
    }
    return false;
    // return this.indexes.get(optionPath).has(optionHash);
  }

  applyFilter(optionPath: PropertyPath, optionHash: Hash) {
    this.appliedFilters.set(optionPath, optionHash);
    const newCurrentRules = new Set<RuleNo>();
    const optionRules = this.indexes.get(optionPath).get(optionHash) ?? new Set<RuleNo>();

    // Optimization, loop through the smallest dataset
    if (optionRules.size < this.currentRules.size) {
      for (let rule of optionRules) {
        if (this.currentRules.has(rule)) {
          newCurrentRules.add(rule);
        }
      }
    } else {
      for (let rule of this.currentRules) {
        if (optionRules.has(rule)) {
          newCurrentRules.add(rule);
        }
      }
    }
    this.currentRules = newCurrentRules;
  }

  resetFilter(optionPath: PropertyPath) {
    if (!this.appliedFilters.has(optionPath)) {
      return;
    }
    this.appliedFilters.delete(optionPath);
    if (this.appliedFilters.size == 0) {
      this.currentRules = this.allRulesSet;
      return true;
    }
    const newCurrentRules = new Set<RuleNo>();
    for (let [path, optionHash] of this.appliedFilters) {
      const rules = this.indexes.get(path).get(optionHash);
      for (let rule of rules) {
        newCurrentRules.add(rule);
      }
    }

    this.currentRules = newCurrentRules;
  }

  anyMatch(): boolean {
    return this.currentRules.size > 0;
  }

  resetAll() {
    this.appliedFilters = new Map<PropertyPath, Hash>();
    this.currentRules = this.allRulesSet;
  }
}
